/**
 * T0704 — Quick Process Panel
 * "I have an idea" entry point calling idea_2_assess_2_x.
 *
 * A simple form: describe what you want → assess → construct in one call.
 * Shows the resulting process with bot count, gap count, and created bots.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Bot, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ArrowRight, Clock } from "lucide-react";
import { WAITING_ON_OWNER, waitingCount } from "./waitingLabels";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ideaToAssessToX,
  getJobStatus,
  type IdeaToProcessRequest,
  type IdeaToProcessResult,
  type IdeaToProcessQueuedResponse,
  type IdeaToProcessResponse,
  type JobStatusResponse,
  type ProcessRecurrence,
} from "@/services/api/deliverableApi";
import type { ApiResponse } from "@/services/api/kernelBotApi";
import { useProcessingJobs } from "@/contexts/ProcessingJobsContext";

interface QuickProcessPanelProps {
  entityId: string;
  entityName?: string;
  onProcessCreated?: (processId: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const EXECUTION_MODES: { value: NonNullable<IdeaToProcessRequest["execution_mode"]>; label: string; hint: string }[] = [
  { value: "MANUAL", label: "Manual", hint: "Prepare everything; I release each step" },
  { value: "SEMI_AUTO", label: "Semi-auto", hint: "Prepare everything; stop before external actions" },
  { value: "FULL_AUTO", label: "Full auto", hint: "Run everything that has no external effect" },
];

const RECURRENCE_PATTERNS: { value: ProcessRecurrence["pattern"]; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom (cron)" },
];

const WEEKDAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

const defaultRecurrence = (): ProcessRecurrence => ({
  enabled: false,
  pattern: "daily",
  time: "09:00",
  interval: 1,
  daysOfWeek: [1],
  dayOfMonth: 1,
  cronExpression: "0 9 * * *",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

export function QuickProcessPanel({ entityId, entityName, onProcessCreated, onDirtyChange, onLoadingChange }: QuickProcessPanelProps) {
  const { addJob } = useProcessingJobs();
  const [description, setDescription] = useState("");
  const [executionMode, setExecutionMode] = useState<IdeaToProcessRequest["execution_mode"]>("SEMI_AUTO");
  const [recurrence, setRecurrence] = useState<ProcessRecurrence>(defaultRecurrence());
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [result, setResult] = useState<IdeaToProcessResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBots, setShowBots] = useState(false);
  const [showGaps, setShowGaps] = useState(false);
  const [jobProcessId, setJobProcessId] = useState<string | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const dirty = loading || polling || description.trim().length > 0 || !!result || recurrence.enabled;
    onDirtyChange?.(dirty);
  }, [loading, polling, description, result, recurrence.enabled, onDirtyChange]);

  useEffect(() => {
    onLoadingChange?.(loading || polling);
  }, [loading, polling, onLoadingChange]);

  useEffect(() => {
    if (!loading && !polling) {
      setElapsedSeconds(0);
      return;
    }
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [loading, polling]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  };

  const handlePollResult = (apiRes: ApiResponse<JobStatusResponse>) => {
    const jobRes = apiRes.data;
    if (!jobRes) return false;
    const final = jobRes.result as IdeaToProcessResult | null;
    if (!final) return false;

    if (final.status === "failed") {
      stopPolling();
      setError(final.message || "Construction failed");
      return true;
    }

    if (final.status === "needs_input") {
      stopPolling();
      setResult(final);
      toast.warning("Idea needs clarification", {
        description: final.assessment?.idea_type
          ? `Classified as "${final.assessment.idea_type}", not a deliverable.`
          : "Could not resolve the idea.",
      });
      return true;
    }

    if (final.status === "done" || final.status === "active" || final.status === "draft") {
      stopPolling();
      setResult(final);
      toast.success("Process constructed", {
        description: `${final.summary?.bots_created ?? 0} bots, ${final.summary?.nodes ?? 0} nodes`,
      });
      const constructedId = final.process_id || jobRes.process_id;
      if (constructedId) {
        onProcessCreated?.(constructedId);
      }
      return true;
    }

    return false;
  };

  const startPolling = (processId: string) => {
    setPolling(true);
    setJobProcessId(processId);

    // Poll immediately once, then every 3 seconds.
    getJobStatus(processId).then((apiRes) => {
      if (handlePollResult(apiRes as ApiResponse<JobStatusResponse>)) return;
    }).catch(() => {
      // Ignore individual poll errors; the interval will retry.
    });

    pollRef.current = setInterval(() => {
      getJobStatus(processId).then((apiRes) => {
        handlePollResult(apiRes as ApiResponse<JobStatusResponse>);
      }).catch(() => {
        // Ignore individual poll errors.
      });
    }, 3000);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await ideaToAssessToX(entityId, {
        description: description.trim(),
        execution_mode: executionMode,
        recurrence: recurrence.enabled ? recurrence : undefined,
      });

      const data = (res as ApiResponse<IdeaToProcessResponse>).data;
      if (!data) {
        setError((res as ApiResponse<IdeaToProcessResponse>).message || "Construction failed");
        return;
      }

      // The BE now returns 202 queued immediately.
      if (data.status === "queued" && data.process_id) {
        const queued = data as IdeaToProcessQueuedResponse;
        setLoading(false);
        startPolling(queued.process_id);
        addJob(queued.process_id, queued.call_id, description.trim());
        toast.info("Raisy is building your process", {
          description: "You can close this dialog — check the floating indicator or Activity Feed.",
        });
        return;
      }

      // Synchronous / already-completed fallback (legacy or very fast construction).
      const final = data as IdeaToProcessResult;
      if (final.status === "needs_input") {
        setResult(final);
        toast.warning("Idea needs clarification", {
          description: final.assessment?.idea_type
            ? `Classified as "${final.assessment.idea_type}", not a deliverable.`
            : "Could not resolve the idea.",
        });
      } else {
        setResult(final);
        toast.success("Process constructed", {
          description: `${final.summary?.bots_created ?? 0} bots, ${final.summary?.nodes ?? 0} nodes`,
        });
        if (final.process_id) {
          onProcessCreated?.(final.process_id);
        }
      }
    } catch (e: any) {
      setError(e?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${rem.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Send className="h-4 w-4" />
        I have an idea
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="text-xs text-muted-foreground shrink-0">Execution mode</label>
        <Select
          value={executionMode}
          onValueChange={(v) => setExecutionMode(v as IdeaToProcessRequest["execution_mode"])}
          disabled={loading}
        >
          <SelectTrigger className="w-full sm:w-[200px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EXECUTION_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value} className="text-xs">
                <div className="flex flex-col">
                  <span>{m.label}</span>
                  <span className="text-label-2xs text-muted-foreground font-normal">{m.hint}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id="recurrent"
          checked={recurrence.enabled}
          onCheckedChange={(checked) =>
            setRecurrence((r) => ({ ...r, enabled: checked === true }))
          }
          disabled={loading}
        />
        <div className="grid gap-1 leading-none">
          <Label htmlFor="recurrent" className="text-xs font-medium">
            Mark this as a recurrent process
          </Label>
          <p className="text-label-2xs text-muted-foreground">
            Raisy will schedule this process to run again automatically.
          </p>
        </div>
      </div>

      {recurrence.enabled && (
        <div className="space-y-3 rounded-md border border-dashed border-border p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Label className="text-xs text-muted-foreground shrink-0">Pattern</Label>
            <Select
              value={recurrence.pattern}
              onValueChange={(v) =>
                setRecurrence((r) => ({ ...r, pattern: v as ProcessRecurrence["pattern"] }))
              }
              disabled={loading}
            >
              <SelectTrigger className="w-full sm:w-[180px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_PATTERNS.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="text-xs">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Label className="text-xs text-muted-foreground shrink-0">Run at</Label>
            <Input
              type="time"
              value={recurrence.time}
              onChange={(e) => setRecurrence((r) => ({ ...r, time: e.target.value }))}
              className="w-full sm:w-[120px] h-8 text-xs"
              disabled={loading}
            />
            <span className="text-label-2xs text-muted-foreground">
              {recurrence.timezone || "local timezone"}
            </span>
          </div>

          {recurrence.pattern === "daily" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground shrink-0">Every</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={recurrence.interval}
                onChange={(e) =>
                  setRecurrence((r) => ({ ...r, interval: Math.max(1, parseInt(e.target.value, 10) || 1) }))
                }
                className="w-16 h-8 text-xs"
                disabled={loading}
              />
              <span className="text-xs text-muted-foreground">day(s)</span>
            </div>
          )}

          {recurrence.pattern === "weekly" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">Every</Label>
                <Input
                  type="number"
                  min={1}
                  max={52}
                  value={recurrence.interval}
                  onChange={(e) =>
                    setRecurrence((r) => ({ ...r, interval: Math.max(1, parseInt(e.target.value, 10) || 1) }))
                  }
                  className="w-16 h-8 text-xs"
                  disabled={loading}
                />
                <span className="text-xs text-muted-foreground">week(s)</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => {
                  const selected = recurrence.daysOfWeek?.includes(d.value) ?? false;
                  return (
                    <button
                      key={d.value}
                      type="button"
                      disabled={loading}
                      onClick={() =>
                        setRecurrence((r) => {
                          const current = r.daysOfWeek ?? [];
                          const next = selected
                            ? current.filter((v) => v !== d.value)
                            : [...current, d.value];
                          return { ...r, daysOfWeek: next.sort() };
                        })
                      }
                      className={cn(
                        "h-7 px-2 rounded-md text-label-2xs font-medium border transition-colors",
                        selected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-transparent border-border text-muted-foreground hover:border-primary/50"
                      )}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {recurrence.pattern === "monthly" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">Every</Label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={recurrence.interval}
                  onChange={(e) =>
                    setRecurrence((r) => ({ ...r, interval: Math.max(1, parseInt(e.target.value, 10) || 1) }))
                  }
                  className="w-16 h-8 text-xs"
                  disabled={loading}
                />
                <span className="text-xs text-muted-foreground">month(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground shrink-0">Day of month</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={recurrence.dayOfMonth}
                  onChange={(e) =>
                    setRecurrence((r) => ({
                      ...r,
                      dayOfMonth: Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)),
                    }))
                  }
                  className="w-16 h-8 text-xs"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {recurrence.pattern === "custom" && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cron expression</Label>
              <Input
                value={recurrence.cronExpression}
                onChange={(e) => setRecurrence((r) => ({ ...r, cronExpression: e.target.value }))}
                placeholder="0 9 * * 1-5"
                className="h-8 text-xs font-mono"
                disabled={loading}
              />
              <p className="text-label-2xs text-muted-foreground">
                Standard cron syntax. Example: 0 9 * * 1-5 = 9am on weekdays.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Textarea
          placeholder={`What should ${entityName || "this entity"} do?\ne.g. "Send an email to an investor"`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-[60px] flex-1"
          disabled={loading || polling}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <Button
          onClick={handleSubmit}
          disabled={loading || polling || !description.trim()}
          className="self-end"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="ml-1 hidden sm:inline">Go</span>
        </Button>
      </div>

      {(loading || polling) && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          <div className="flex-1">
            <p className="font-medium">Raisy is building your process</p>
            <p className="text-label-2xs text-amber-700/80">
              This usually takes 10–180 seconds. Elapsed: {formatElapsed(elapsedSeconds)}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-3 rounded-md border bg-card p-4"
          >
            {/* Assessment short-circuit */}
            {result.status === "needs_input" && result.assessment && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Not an outcome</p>
                  <p>Classified as "{result.assessment.idea_type}" (confidence: {result.assessment.confidence?.toFixed(2)}).</p>
                  {result.questions && result.questions.length > 0 && (
                    <ul className="mt-1 list-disc pl-4">
                      {result.questions.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                  )}
                  <p className="mt-1 text-xs">Try refining your description or use the full explorer instead.</p>
                </div>
              </div>
            )}

            {/* Summary */}
            {result.summary && (
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="gap-1">
                  <Bot className="h-3 w-3" />
                  {result.summary.bots_created} bots
                </Badge>
                <Badge variant="outline">{result.summary.kernels_created} kernels</Badge>
                <Badge variant="outline">{result.summary.nodes} nodes</Badge>
                {result.summary.gaps > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {waitingCount(result.summary.gaps)}
                  </Badge>
                )}
                {result.process_id && (
                  <Badge variant="secondary">process: {result.process_id.slice(0, 20)}…</Badge>
                )}
              </div>
            )}

            {/* Created bots */}
            {result.created_bots && result.created_bots.length > 0 && (
              <div>
                <button
                  onClick={() => setShowBots(!showBots)}
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  {showBots ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  Created Bots ({result.created_bots.length})
                </button>
                {showBots && (
                  <div className="mt-2 space-y-1">
                    {result.created_bots.map((b) => (
                      <div key={b.bot_id} className="rounded border px-2 py-1 text-xs">
                        <span className="font-mono font-medium">{b.bot_key}</span>
                        <span className="ml-2 text-muted-foreground">{b.classification}</span>
                        <span className="ml-2 text-muted-foreground">({b.kernel_keys.length} kernels)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Questions for you — the process is waiting on the owner, and the label says so (R-P2.9). */}
            {result.gaps && result.gaps.length > 0 && (
              <div>
                <button
                  onClick={() => setShowGaps(!showGaps)}
                  className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                >
                  {showGaps ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {WAITING_ON_OWNER} ({result.gaps.length})
                </button>
                {showGaps && (
                  <div className="mt-2 space-y-1">
                    {result.gaps.map((g) => (
                      <div key={g.gap_id} className="flex items-start gap-2 rounded border border-red-100 bg-red-50 px-2 py-1 text-xs">
                        <AlertTriangle className="h-3 w-3 shrink-0 text-red-500 mt-0.5" />
                        <div>
                          <p>{g.description}</p>
                          {g.recommended_action && (
                            <p className="text-muted-foreground">→ {g.recommended_action}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {result.status === "done" && result.process_id && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  Process ready — review in the process map.
                </div>
                {onProcessCreated && (
                  <button
                    onClick={() => onProcessCreated(result.process_id!)}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    View process map
                    <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
